import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import AdminLayout from './components/admin/AdminLayout';
import AdminRoute from './components/AdminRoute';
import ManagerRedirect from './components/ManagerRedirect';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/LoadingSpinner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Lazy load pages for code splitting and performance optimization
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Venues = lazy(() => import('./pages/Venues'));
const VenueDetail = lazy(() => import('./pages/VenueDetail'));
const CourtDetails = lazy(() => import('./pages/CourtDetails'));
const Offers = lazy(() => import('./pages/Offers'));
const Search = lazy(() => import('./pages/Search'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const CoachingMarketplace = lazy(() => import('./pages/CoachingMarketplace'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const TournamentDetail = lazy(() => import('./pages/TournamentDetail'));
const Bookings = lazy(() => import('./pages/Bookings'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Profile = lazy(() => import('./pages/Profile'));
const SellProduct = lazy(() => import('./pages/SellProduct'));
const MyProducts = lazy(() => import('./pages/MyProducts'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Lazy load admin pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminBookings = lazy(() => import('./pages/admin/Bookings'));
const AdminInvoices = lazy(() => import('./pages/admin/Invoices'));
const AdminCourts = lazy(() => import('./pages/admin/Courts'));
const AdminBookingCalendar = lazy(() => import('./pages/admin/AdminBookingCalendar'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const AdminReviews = lazy(() => import('./pages/admin/Reviews'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminManagers = lazy(() => import('./pages/admin/AdminManagers'));
const SuperAdmin = lazy(() => import('./pages/admin/SuperAdmin'));
const CoachApprovalManagement = lazy(() => import('./pages/admin/CoachApprovalManagement'));
const AdminTournaments = lazy(() => import('./pages/admin/Tournaments'));
const TournamentApplications = lazy(() => import('./pages/admin/TournamentApplications'));

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ScrollToTop />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Navbar />
          <main>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/venues" element={<Venues />} />
            <Route path="/courts/:id" element={<CourtDetails />} />
            <Route path="/venues/:id" element={<VenueDetail />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/search" element={<Search />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/:id" element={<ProductDetail />} />
            <Route path="/coaching" element={<CoachingMarketplace />} />
            <Route path="/tournaments" element={<Tournaments />} />
            <Route path="/tournaments/:id" element={<TournamentDetail />} />
            
            {/* Protected routes */}
            <Route path="/bookings" element={
              <ProtectedRoute>
                <Bookings />
              </ProtectedRoute>
            } />
            <Route path="/favorites" element={
              <ProtectedRoute>
                <Favorites />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/marketplace/sell" element={
              <ProtectedRoute>
                <SellProduct />
              </ProtectedRoute>
            } />
            <Route path="/marketplace/my-products" element={
              <ProtectedRoute>
                <MyProducts />
              </ProtectedRoute>
            } />
            
            
            {/* Admin routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <ManagerRedirect>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </ManagerRedirect>
              </AdminRoute>
            } />
            <Route path="/admin/bookings" element={
              <AdminRoute requiredRoles={['court_admin', 'super_admin']}>
                <AdminLayout>
                  <AdminBookings />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/invoices" element={
              <AdminRoute>
                <AdminLayout>
                  <AdminInvoices />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/courts" element={
              <AdminRoute>
                <AdminLayout>
                  <AdminCourts />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/courts/:courtId/bookings" element={
              <AdminRoute>
                <AdminLayout>
                  <AdminBookingCalendar />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/analytics" element={
              <AdminRoute requiredRoles={['court_admin', 'super_admin']}>
                <AdminLayout>
                  <Analytics />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/reviews" element={
              <AdminRoute requiredRoles={['super_admin']}>
                <AdminLayout>
                  <AdminReviews />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/products" element={
              <AdminRoute requiredRoles={['super_admin']}>
                <AdminLayout>
                  <AdminProducts />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/managers" element={
              <AdminRoute requiredRoles={['court_admin', 'super_admin']}>
                <AdminLayout>
                  <AdminManagers />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/super" element={
              <AdminRoute requiredRoles={['super_admin']}>
                <AdminLayout>
                  <SuperAdmin />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/coach-approval" element={
              <AdminRoute requiredRoles={['super_admin']}>
                <AdminLayout>
                  <CoachApprovalManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/tournaments" element={
              <AdminRoute>
                <AdminLayout>
                  <AdminTournaments />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/tournament-applications" element={
              <AdminRoute>
                <AdminLayout>
                  <TournamentApplications />
                </AdminLayout>
              </AdminRoute>
            } />
            
                {/* 404 route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
